import { useViewRuntimeContext } from "@/components/common/view-runtime-context";
import {
  CameraParameters,
  decodeCameraParameters,
  encodeCameraParameters,
  getCameraParameters,
} from "@/components/r3f/utils/camera-parameters";
import { useTreeContext } from "@/components/ui/tree/tree-context";
import {
  InitialStateProps,
  ModeWithSceneFilterInitialState,
} from "@/modes/mode";
import { useCurrentArea, useCurrentScene } from "@/modes/mode-data-context";
import { CurrentScene, selectCurrentScene } from "@/modes/mode-selectors";
import { selectWalkSceneElements } from "@/modes/walk-mode/walk-scene-selectors";
import { WALK_MODE_INITIAL_STATE } from "@/modes/walk-mode/walk-state";
import { WalkSceneActiveElement } from "@/modes/walk-mode/walk-types";
import { Features, selectHasFeature } from "@/store/features/features-slice";
import { selectFlyWalkSceneFilter } from "@/store/modes/flywalk-mode-slice";
import {
  selectCompareElement,
  selectCompareShouldUseIntensityData,
  selectShouldSyncCamerasOnWaypoint,
  selectShouldSyncCamerasRotation,
  selectWalkCompareSceneFilter,
} from "@/store/modes/walk-mode-slice";
import { useAppSelector } from "@/store/store-hooks";
import { SceneFilter, usesCadModel } from "@/types/scene-filter";
import { GUID, assert, includes } from "@faro-lotv/foundation";
import { isIElementGenericPointCloudStream } from "@faro-lotv/ielement-types";
import { selectIElement } from "@faro-lotv/project-source";
import { isEqual } from "es-toolkit";
import { useMemo } from "react";
import { Vector3 } from "three";

/** The composed state for the split screen mode accounting for both views */
export type SplitState = {
  /** The scene definition for the main view */
  mainScene: CurrentScene;

  /** The scene filter in the main view */
  mainSceneFilter: SceneFilter;

  /** The main element to be rendered on left scene */
  mainWalkElement: WalkSceneActiveElement;

  /** The scene definition for the secondary view */
  compareScene: CurrentScene;

  /** The scene filter in the secondary view */
  compareSceneFilter: SceneFilter;

  /** The element to render on the right scene as comparison element */
  compareWalkElement: WalkSceneActiveElement;
};

/**
 * @returns the main and comparison element data for split screen interaction
 */
export function useSplitState(): SplitState {
  const area = useCurrentArea();
  const mainScene = useCurrentScene();

  const compareElement =
    useAppSelector(selectCompareElement) ?? mainScene.activeElement;

  assert(
    compareElement,
    "To start split screen we need a valid object to compare",
  );

  const compareShouldUseIntensityData = useAppSelector(
    selectCompareShouldUseIntensityData,
  );
  const { tree } = useTreeContext();

  const activeCad = mainScene.cad;
  const mainSceneFilter = useAppSelector(selectFlyWalkSceneFilter);
  const storedCompareSceneFilter = useAppSelector(selectWalkCompareSceneFilter);

  // The rendering pipeline is not yet able to render the cad model in two scenes at the same time,
  // so the main view takes precedence: the compare scene can only access the cad model while the
  // main view is not using it, and falls back to a cad-free filter otherwise.
  const mainUsesCad = usesCadModel(mainSceneFilter);

  const hasFlySplitScreen = useAppSelector(
    selectHasFeature(Features.FlySplitScreen),
  );

  const compareCad = !hasFlySplitScreen || mainUsesCad ? undefined : activeCad;
  const compareSceneFilter = computeCompareSceneFilter(
    mainSceneFilter,
    storedCompareSceneFilter,
    isIElementGenericPointCloudStream(compareElement)
      ? SceneFilter.PointCloud
      : SceneFilter.Pano,
  );

  const compareScene = useAppSelector(
    selectCurrentScene(
      compareElement,
      compareCad,
      area,
      tree,
      compareShouldUseIntensityData,
    ),
    isEqual,
  );

  assert(
    compareScene,
    "A valid render object for both scenes is required for split mode",
  );

  const { cameras } = useViewRuntimeContext();

  const leftCameraPosition = useMemo(
    () => cameras[0]?.position ?? new Vector3(),
    [cameras],
  );
  const rightCameraPosition = useMemo(
    () => cameras[1]?.position ?? leftCameraPosition,
    [cameras, leftCameraPosition],
  );

  // Determine the walk elements for both scenes
  const [mainWalkElement] = useAppSelector(
    selectWalkSceneElements(
      mainScene,
      area,
      leftCameraPosition,
      mainSceneFilter,
    ),
    isEqual,
  );

  const [compareWalkElement] = useAppSelector(
    selectWalkSceneElements(
      compareScene,
      area,
      rightCameraPosition,
      compareSceneFilter,
    ),
    isEqual,
  );
  assert(
    mainWalkElement && compareWalkElement,
    "A valid render split screen both scenes should have a valid element",
  );

  return {
    mainScene,
    mainSceneFilter,
    mainWalkElement,
    compareScene,
    compareSceneFilter,
    compareWalkElement,
  };
}

export type SplitInitialState = ModeWithSceneFilterInitialState & {
  /** The id of the compare element */
  rightId: GUID;
  /** The camera parameters for the right screen */
  rightCam?: CameraParameters;
  /** The active scene filter */
  rightScene: SceneFilter;
  /** Whether to sync cameras rotations */
  syncRot?: string;
  /** Whether to sync cameras on the selected waypoint*/
  syncWaypoint?: string;
};

/** An object to compute and parse a state object to create and restore the walk mode states from deep links */
export const SPLIT_MODE_INITIAL_STATE: InitialStateProps<SplitInitialState> = {
  compute(state, viewContext) {
    return {
      ...WALK_MODE_INITIAL_STATE.compute(state, viewContext),
      rightId: selectCompareElement(state)?.id ?? "",
      rightCam: encodeCameraParameters(
        getCameraParameters(viewContext.cameras[1]),
      ),
      rightScene: selectWalkCompareSceneFilter(state),
      syncRot: selectShouldSyncCamerasRotation(state) ? "1" : "0",
      syncWaypoint: selectShouldSyncCamerasOnWaypoint(state) ? "1" : "0",
    };
  },
  parse(appState, modeState) {
    const walkState = WALK_MODE_INITIAL_STATE.parse(appState, modeState);
    const {
      rightId,
      rightCam,
      syncRot: uncheckedSyncRot,
      syncWaypoint: uncheckedSyncWaypoint,
    } = modeState;
    // 'syncRot' and 'syncWaypoint' are expected to be either "0" or "1" in the deep link.
    // Any other string is invalid and will be treated as undefined.
    const syncRot = validateZeroOrOne(uncheckedSyncRot);
    const syncWaypoint = validateZeroOrOne(uncheckedSyncWaypoint);

    if (!walkState || !rightId || typeof rightId !== "string") {
      return;
    }
    if (!selectIElement(rightId)(appState)) {
      throw new Error(
        "Invalid SplitMode deep link: comparison element id does not exist",
      );
    }

    // The legacy split screen cannot render Gaussian splats
    const canRenderSplats = selectHasFeature(Features.FlySplitScreen)(appState);
    const scene =
      walkState.scene && toRenderableFilter(walkState.scene, canRenderSplats);

    const parsedRightScene = toRenderableFilter(
      includes(Object.values(SceneFilter), modeState.rightScene)
        ? modeState.rightScene
        : SceneFilter.Pano,
      canRenderSplats,
    );

    const rightScene = scene
      ? computeCompareSceneFilter(scene, parsedRightScene, SceneFilter.Pano)
      : parsedRightScene;

    return {
      ...walkState,
      scene,
      rightId,
      rightCam: rightCam ? decodeCameraParameters(rightCam) : undefined,
      rightScene,
      syncRot,
      syncWaypoint,
    };
  },
};

/**
 * @param filter the scene filter to render
 * @param canRenderSplats whether the split screen implementation can render Gaussian splats
 * @returns the given filter, or the panorama when it cannot be rendered
 */
function toRenderableFilter(
  filter: SceneFilter,
  canRenderSplats: boolean,
): SceneFilter {
  return !canRenderSplats && filter === SceneFilter.GaussianSplats
    ? SceneFilter.Pano
    : filter;
}

/**
 * The cad model and the Gaussian splats can be rendered in only one of the two views at a time.
 * The main view takes precedence, so the compare view falls back to the panorama on a conflict.
 *
 * @param mainSceneFilter the scene filter of the main view
 * @param compareSceneFilter the desired scene filter of the compare view
 * @param suggestedSceneFilter the scene filter to adopt in case it cannot be the same as the main
 * @returns the scene filter the compare view can use
 */
function computeCompareSceneFilter(
  mainSceneFilter: SceneFilter,
  compareSceneFilter: SceneFilter,
  suggestedSceneFilter: SceneFilter,
): SceneFilter {
  const bothUseCad =
    usesCadModel(mainSceneFilter) && usesCadModel(compareSceneFilter);
  const bothUseSplats =
    mainSceneFilter === SceneFilter.GaussianSplats &&
    compareSceneFilter === SceneFilter.GaussianSplats;
  return bothUseCad || bothUseSplats
    ? suggestedSceneFilter
    : compareSceneFilter;
}

function validateZeroOrOne(value: string | undefined): string | undefined {
  if (value === "0" || value === "1") {
    return value;
  }
}
